import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Button, TextField, Dialog, DialogTitle, DialogContent,
    DialogActions, IconButton, CircularProgress, Alert, Switch, FormControlLabel
} from '@mui/material';
import { styled } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'; // New Icon
import axios from 'axios';

// Import Drag and Drop components
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

const API_BASE_URL = process.env.REACT_APP_API_BASE || 'http://localhost:5000';

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
}));

const PaymentMethod = () => {
    const [methods, setMethods] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [selectedMethod, setSelectedMethod] = useState(null);
    const [actionType, setActionType] = useState('add');

    const fetchMethods = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('jwtToken');
            const { data } = await axios.get(`${API_BASE_URL}/api/admin/payment-methods`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            // Ensure data is sorted by your 'order' field from the backend
            setMethods(data.sort((a, b) => a.order - b.order));
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch payment methods.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMethods();
    }, []);

    // --- Drag and Drop Logic ---
    const onDragEnd = async (result) => {
        if (!result.destination) return;

        const items = Array.from(methods);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        // Optimistically update UI
        setMethods(items);

        try {
            const token = localStorage.getItem('jwtToken');
            // Send the entire reordered list or just the new sequence to the backend
            await axios.put(`${API_BASE_URL}/api/admin/payment-methods/reorder`, 
                { methodIds: items.map(m => m.id) }, 
                { headers: { Authorization: `Bearer ${token}` } }
            );
        } catch (err) {
            setError("Failed to save new order.");
            fetchMethods(); // Revert on error
        }
    };

    // ... (Keep your handleOpen/Close/Delete functions the same)
    const handleOpenAddDialog = () => { setActionType('add'); setSelectedMethod({ label: '', is_active: true }); setDialogOpen(true); };
    const handleOpenEditDialog = (item) => { setActionType('edit'); setSelectedMethod(item); setDialogOpen(true); };
    const handleCloseDialog = () => { setDialogOpen(false); setSelectedMethod(null); };
    const handleOpenConfirmDialog = (item) => { setSelectedMethod(item); setConfirmDialogOpen(true); };
    const handleCloseConfirmDialog = () => { setConfirmDialogOpen(false); setSelectedMethod(null); };

    const handleSave = async () => {
        try {
            const token = localStorage.getItem('jwtToken');
            const headers = { Authorization: `Bearer ${token}` };
            if (actionType === 'add') {
                await axios.post(`${API_BASE_URL}/api/admin/payment-methods`, selectedMethod, { headers });
            } else {
                await axios.put(`${API_BASE_URL}/api/admin/payment-methods/${selectedMethod.id}`, selectedMethod, { headers });
            }
            fetchMethods();
            handleCloseDialog();
        } catch (err) {
            setError(err.response?.data?.message || `Failed to ${actionType} payment method.`);
        }
    };

    const handleDelete = async () => {
        try {
            const token = localStorage.getItem('jwtToken');
            await axios.delete(`${API_BASE_URL}/api/admin/payment-methods/${selectedMethod.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchMethods();
            handleCloseConfirmDialog();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete payment method.');
        }
    };

    if (loading) return <CircularProgress />;

    return (
        <Box>
            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
            <StyledPaper>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Manage Payment Methods (Drag to Reorder)</Typography>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAddDialog}>
                        Add Method
                    </Button>
                </Box>

                <DragDropContext onDragEnd={onDragEnd}>
                    <TableContainer>
                        <Table size="small">
                            <TableHead sx={{ backgroundColor: '#11224d' }}>
                                <TableRow>
                                    <TableCell sx={{ color: 'white', width: 50 }}></TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Label</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'right' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            
                            <Droppable droppableId="payment-methods">
                                {(provided) => (
                                    <TableBody {...provided.droppableProps} ref={provided.innerRef}>
                                        {methods.map((item, index) => (
                                            <Draggable key={item.id.toString()} draggableId={item.id.toString()} index={index}>
                                                {(provided, snapshot) => (
                                                    <TableRow
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        sx={{ 
                                                            ...provided.draggableProps.style,
                                                            backgroundColor: snapshot.isDragging ? '#f5f5f5' : 'inherit',
                                                            display: snapshot.isDragging ? 'table' : 'table-row' // Fixes table width collapse while dragging
                                                        }}
                                                        hover
                                                    >
                                                        <TableCell {...provided.dragHandleProps}>
                                                            <DragIndicatorIcon color="action" />
                                                        </TableCell>
                                                        <TableCell>{item.label}</TableCell>
                                                        <TableCell>{item.is_active ? 'Active' : 'Inactive'}</TableCell>
                                                        <TableCell align="right">
                                                            <IconButton size="small" onClick={() => handleOpenEditDialog(item)}>
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                            <IconButton size="small" onClick={() => handleOpenConfirmDialog(item)}>
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </TableBody>
                                )}
                            </Droppable>
                        </Table>
                    </TableContainer>
                </DragDropContext>
            </StyledPaper>

            {/* ... (Keep your Dialogs the same) */}
            <Dialog open={dialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="xs">
                <DialogTitle>{actionType === 'add' ? 'Add New' : 'Edit'} Payment Method</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus margin="dense" label="Label" fullWidth variant="outlined"
                        value={selectedMethod?.label || ''}
                        onChange={(e) => setSelectedMethod({ ...selectedMethod, label: e.target.value })}
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={selectedMethod?.is_active || false}
                                onChange={(e) => setSelectedMethod({ ...selectedMethod, is_active: e.target.checked })}
                            />
                        }
                        label="Active"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained">Save</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={confirmDialogOpen} onClose={handleCloseConfirmDialog}>
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete the method "{selectedMethod?.label}"? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseConfirmDialog}>Cancel</Button>
                    <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PaymentMethod;